// src/pages/GameListPage.js
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom'; // for reading URL query
import GameCard from './components/GameCard';
import AdminLayout from './components/AdminLayout';

// Categories listed here are pinned to the front of the tab bar, in this order.
// Anything else found in the data is appended after, in the order it appears.
const CATEGORY_PRIORITY = ['Cash Coupon'];

const GameListPage = () => {
  const [games, setGames] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  // Get query params (abc & xyz)
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const abc = queryParams.get('abc');
  const xyz = queryParams.get('xyz');

  // Fetch data from API only if abc & xyz exist
  useEffect(() => {
    const fetchGames = async () => {
      if (!abc || !xyz) {
        setError('Missing abc or xyz parameter in URL');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(
          `https://api.gamegems.co/web/games/list?abc=${abc}&xyz=${xyz}`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch games');
        }

        const data = await response.json();
        setGames(data?.data || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchGames();
  }, [abc, xyz]);

  // Build tab list from whatever categories are present in the fetched games,
  // with CATEGORY_PRIORITY entries pinned to the front.
  const categories = React.useMemo(() => {
    const set = new Set();
    games.forEach((game) => {
      const cat = game.genre || game.category;
      if (cat) set.add(cat);
    });
    const sorted = Array.from(set).sort((a, b) => {
      const ai = CATEGORY_PRIORITY.indexOf(a);
      const bi = CATEGORY_PRIORITY.indexOf(b);
      if (ai === -1 && bi === -1) return 0;
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
    return ['All', ...sorted];
  }, [games]);

  // If the active tab's category disappears from the data, fall back to "All"
  useEffect(() => {
    if (activeCategory !== 'All' && !categories.includes(activeCategory)) {
      setActiveCategory('All');
    }
  }, [categories, activeCategory]);

  const filteredGames = games.filter((game) => {
    const title = game.title || game.name || '';
    const matchesSearch = title.toLowerCase().includes(searchTerm.toLowerCase());
    const cat = game.genre || game.category;
    const matchesCategory = activeCategory === 'All' || cat === activeCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <AdminLayout abc={abc} xyz={xyz}>
      <div className="p-3 sm:p-4 md:p-6">
        <h1 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-white page-title">
          Marketplace
        </h1>

        <input
          type="text"
          placeholder="Search games..."
          className="w-full p-2 pl-3 text-base border border-gray-900 text-white rounded-full mb-4 focus:outline-none focus:ring-2 focus:ring-indigo-400"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        {categories.length > 1 && (
          <div className="flex gap-2 overflow-x-auto no-scrollbar mb-4 sm:mb-6 -mx-3 px-3 sm:mx-0 sm:px-0">
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setActiveCategory(cat)}
                className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition whitespace-nowrap ${
                  activeCategory === cat
                    ? 'bg-white text-black'
                    : 'border border-gray-800 text-gray-400 hover:text-white hover:border-gray-600'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <p className="text-center text-gray-400">Loading games...</p>
        ) : error ? (
          <p className="text-center text-red-500">{error}</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
            {filteredGames.map((game) => (
              <GameCard key={game.id} game={game} abc={abc} xyz={xyz} />
            ))}
          </div>
        )}

        {!loading && filteredGames.length === 0 && (
          <p className="text-center text-gray-500 mt-6">No games found.</p>
        )}
      </div>
    </AdminLayout>
  );
};

export default GameListPage;
